import React, { useState } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { Eye, EyeOff, UserPlus, AlertCircle, CheckCircle2, Building2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Alert, AlertDescription } from '../ui/alert';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { ScrollArea } from '../ui/scroll-area';
import { db } from '../../lib/db';
import { useUsers } from '../../hooks/useDatabase';
import { STATE_NAMES } from '../../lib/usPayrollData';
import bcrypt from 'bcryptjs';
import { v4 as uuidv4 } from 'uuid';

export default function Register() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const inviteCode = searchParams.get('code') || '';

  const [formData, setFormData] = useState({
    inviteCode: inviteCode,
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
    department: '',
    position: '',
    location: '',
    phone: '',
    gender: '',
    dateOfBirth: '',
    hireDate: '',
    workState: '',
    residentState: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleChange = (field, value) => {
    setFormData(prev => {
      const updates = { ...prev, [field]: value };
      // Auto-set resident state when work state is selected and resident state is empty
      if (field === 'workState' && !prev.residentState) {
        updates.residentState = value;
      }
      return updates;
    });
  };

  const validatePassword = (password) => {
    const checks = [
      { test: password.length >= 8, label: 'At least 8 characters' },
      { test: /[A-Z]/.test(password), label: 'One uppercase letter' },
      { test: /[a-z]/.test(password), label: 'One lowercase letter' },
      { test: /[0-9]/.test(password), label: 'One number' },
      { test: /[^A-Za-z0-9]/.test(password), label: 'One special character' }
    ];
    return checks;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    // Validate passwords match
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      setIsLoading(false);
      return;
    }

    // Validate password strength
    const passwordChecks = validatePassword(formData.password);
    if (!passwordChecks.every(c => c.test)) {
      setError('Password does not meet requirements');
      setIsLoading(false);
      return;
    }

    try {
      // Validate invite code
      const invites = await db.invites.toArray();
      const invite = invites.find(i => 
        i.inviteCode === formData.inviteCode && 
        i.status === 'active' &&
        i.expiryDate > Date.now() &&
        i.usedCount < i.maxUses
      );

      if (!invite) {
        setError('Invalid or expired invite code');
        setIsLoading(false);
        return;
      }

      // Check if email already exists
      const existingUser = await db.users.where('email').equals(formData.email).first();
      if (existingUser) {
        setError('An account with this email already exists');
        setIsLoading(false);
        return;
      }

      // Find admin for reference
      const admins = await db.users.where('role').equals('admin').toArray();
      const admin = admins.find(a => a.status === 'active');

      // Use supervisor from invite if specified, otherwise find first available
      let assignedSupervisorId = invite.supervisorId || null;
      if (!assignedSupervisorId && invite.role === 'employee') {
        const supervisors = await db.users.where('role').equals('supervisor').toArray();
        const activeSupervisor = supervisors.find(s => s.status === 'active');
        assignedSupervisorId = activeSupervisor?.id || null;
      }

      // Hash password
      const passwordHash = await bcrypt.hash(formData.password, 10);

      // Create user
      const newUser = {
        id: uuidv4(),
        email: formData.email,
        passwordHash,
        role: invite.role || 'employee',
        adminId: admin?.id || null,
        supervisorId: assignedSupervisorId,
        profile: {
          firstName: formData.firstName,
          lastName: formData.lastName,
          photo: '',
          department: invite.department && invite.department !== 'any' ? invite.department : formData.department,
          position: formData.position,
          location: formData.location,
          phone: formData.phone,
          gender: formData.gender,
          dateOfBirth: formData.dateOfBirth ? new Date(formData.dateOfBirth).getTime() : null,
          hireDate: formData.hireDate ? new Date(formData.hireDate).getTime() : Date.now(),
          workState: formData.workState || '',
          residentState: formData.residentState || formData.workState || ''
        },
        status: 'active',
        onboardingComplete: false, // New users must complete onboarding
        createdAt: Date.now(),
        updatedAt: Date.now()
      };

      await db.users.add(newUser);

      // Update invite usage count
      await db.invites.update(invite.id, { 
        usedCount: invite.usedCount + 1,
        // If max uses reached, mark as expired
        ...(invite.usedCount + 1 >= invite.maxUses ? { status: 'expired' } : {})
      });

      setSuccess(true);
      setTimeout(() => navigate('/login'), 2000);
    } catch (err) {
      console.error('Registration error:', err);
      setError(err.message || 'Registration failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const passwordChecks = validatePassword(formData.password);

  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-teal-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-md border-0 shadow-xl">
          <CardContent className="pt-8 pb-8 text-center">
            <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle2 className="w-8 h-8 text-emerald-600" />
            </div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Registration Successful!</h2>
            <p className="text-gray-500">Redirecting you to login...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-teal-50 flex items-center justify-center p-4">
      <div className="w-full max-w-lg">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-14 h-14 bg-gradient-to-br from-teal-500 to-emerald-600 rounded-2xl shadow-lg shadow-teal-500/25 mb-4">
            <span className="text-white font-bold text-xl">P</span>
          </div>
          <h1 className="text-2xl font-bold text-gray-900">Join PerformanceHub</h1>
          <p className="text-gray-500 mt-1">Create your account</p>
        </div>

        <Card className="border-0 shadow-xl shadow-gray-200/50">
          <CardHeader className="space-y-1 pb-4">
            <CardTitle className="text-xl">Register</CardTitle>
            <CardDescription>Enter your invite code and details</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              {error && (
                <Alert variant="destructive" className="bg-rose-50 border-rose-200">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              <div className="space-y-2">
                <Label htmlFor="inviteCode">Invite Code *</Label>
                <Input
                  id="inviteCode"
                  placeholder="INV-XXXX-XXXX"
                  value={formData.inviteCode}
                  onChange={(e) => handleChange('inviteCode', e.target.value)}
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="firstName">First Name *</Label>
                  <Input
                    id="firstName"
                    placeholder="John"
                    value={formData.firstName}
                    onChange={(e) => handleChange('firstName', e.target.value)}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="lastName">Last Name *</Label>
                  <Input
                    id="lastName"
                    placeholder="Doe"
                    value={formData.lastName}
                    onChange={(e) => handleChange('lastName', e.target.value)}
                    required
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="email">Email *</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="john.doe@company.com"
                  value={formData.email}
                  onChange={(e) => handleChange('email', e.target.value)}
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="department">Department *</Label>
                  <Select value={formData.department} onValueChange={(v) => handleChange('department', v)}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="engineering">Engineering</SelectItem>
                      <SelectItem value="marketing">Marketing</SelectItem>
                      <SelectItem value="sales">Sales</SelectItem>
                      <SelectItem value="hr">HR</SelectItem>
                      <SelectItem value="finance">Finance</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="gender">Gender</Label>
                  <Select value={formData.gender} onValueChange={(v) => handleChange('gender', v)}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="male">Male</SelectItem>
                      <SelectItem value="female">Female</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="position">Position *</Label>
                  <Input
                    id="position"
                    placeholder="Your role"
                    value={formData.position}
                    onChange={(e) => handleChange('position', e.target.value)}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="location">Location *</Label>
                  <Input
                    id="location"
                    placeholder="Office/Remote"
                    value={formData.location}
                    onChange={(e) => handleChange('location', e.target.value)}
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="phone">Phone *</Label>
                  <Input
                    id="phone"
                    type="tel"
                    placeholder="555-0100"
                    value={formData.phone}
                    onChange={(e) => handleChange('phone', e.target.value)}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="dateOfBirth">Date of Birth</Label>
                  <Input
                    id="dateOfBirth"
                    type="date"
                    value={formData.dateOfBirth}
                    onChange={(e) => handleChange('dateOfBirth', e.target.value)}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="hireDate">Date of Joining</Label>
                <Input
                  id="hireDate"
                  type="date"
                  value={formData.hireDate}
                  onChange={(e) => handleChange('hireDate', e.target.value)}
                />
              </div>

              {/* US Tax Information */}
              <div className="rounded-lg border border-teal-200 bg-teal-50/50 p-4 space-y-4">
                <div className="flex items-center gap-2 text-teal-700">
                  <Building2 className="w-4 h-4" />
                  <span className="text-sm font-medium">US Tax Information</span>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="workState">Work State *</Label>
                    <Select value={formData.workState} onValueChange={(v) => handleChange('workState', v)}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select state..." />
                      </SelectTrigger>
                      <SelectContent>
                        <ScrollArea className="h-[200px]">
                          {Object.entries(STATE_NAMES).map(([code, name]) => (
                            <SelectItem key={code} value={code}>
                              {name} ({code})
                            </SelectItem>
                          ))}
                        </ScrollArea>
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-gray-500">State where you work</p>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="residentState">Resident State</Label>
                    <Select value={formData.residentState} onValueChange={(v) => handleChange('residentState', v)}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select state..." />
                      </SelectTrigger>
                      <SelectContent>
                        <ScrollArea className="h-[200px]">
                          {Object.entries(STATE_NAMES).map(([code, name]) => (
                            <SelectItem key={code} value={code}>
                              {name} ({code})
                            </SelectItem>
                          ))}
                        </ScrollArea>
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-gray-500">State where you live</p>
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="password">Password *</Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="••••••••"
                    value={formData.password}
                    onChange={(e) => handleChange('password', e.target.value)}
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                {formData.password && (
                  <div className="mt-2 space-y-1">
                    {passwordChecks.map((check, i) => (
                      <div key={i} className={`text-xs flex items-center gap-1 ${check.test ? 'text-emerald-600' : 'text-gray-400'}`}>
                        <CheckCircle2 className="w-3 h-3" />
                        {check.label}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirmPassword">Confirm Password *</Label>
                <Input
                  id="confirmPassword"
                  type="password"
                  placeholder="••••••••"
                  value={formData.confirmPassword}
                  onChange={(e) => handleChange('confirmPassword', e.target.value)}
                  required
                />
              </div>

              <Button 
                type="submit" 
                className="w-full h-11 bg-gradient-to-r from-teal-500 to-emerald-600 hover:from-teal-600 hover:to-emerald-700 text-white"
                disabled={isLoading}
              >
                {isLoading ? (
                  <span className="flex items-center gap-2">
                    <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Creating account...
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    <UserPlus className="w-4 h-4" /> Create Account
                  </span>
                )}
              </Button>
            </form>

            <div className="mt-6 text-center">
              <p className="text-sm text-gray-500">
                Already have an account?{' '}
                <Link to="/login" className="text-teal-600 hover:text-teal-700 font-medium">
                  Sign in
                </Link>
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
